import { rd, type RemoteData } from '@passionware/monads';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createContactDocument,
  createContactNote,
  createContactPin,
  deleteContactDocument,
  deleteContactNote,
  deleteContactPin,
  getContactAssociations,
  reorderContactDocuments,
  updateContactPin,
  updateContactPins
} from 'store/v1/contacts/contacts.actions.js';
import type { FilesPanelDocument } from 'v5/patterns/widgets/attachments/FilesPanel';
import type { NotesPanelNote } from 'v5/patterns/widgets/attachments/NotesPanel';
import { type MediaPin } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.types';

/** What the v1 store keeps per resource under `contacts.associations_data`. */
export type ResourceAssociations = {
  pins?: MediaPin[];
  notes?: NotesPanelNote[];
  documents?: FilesPanelDocument[];
};

interface ContactsAssociationsSlice {
  associations_data?: Record<number, ResourceAssociations>;
  loading?: boolean | string;
  error?: boolean;
}

/** The resource's associations from the v1 store, fetched when missing. */
export function useResourceAssociations(
  resourceId: number
): RemoteData<ResourceAssociations> {
  const dispatch = useDispatch();
  const { associations, isContactsLoading, contactsError } = useSelector(
    (state: { contacts?: ContactsAssociationsSlice }) => {
      const contacts = state.contacts;
      const associationsData = contacts?.associations_data;

      return {
        associations: associationsData?.[resourceId],
        isContactsLoading: Boolean(contacts?.loading),
        contactsError: Boolean(contacts?.error)
      };
    }
  );

  useEffect(() => {
    if (!associations && !isContactsLoading) {
      dispatch(getContactAssociations(resourceId));
    }
  }, [dispatch, resourceId, associations, isContactsLoading]);

  return useMemo((): RemoteData<ResourceAssociations> => {
    if (associations !== undefined) {
      return rd.of(associations);
    }
    if (contactsError) {
      return rd.ofError(new Error('Failed to load resource associations'));
    }
    return rd.ofPending();
  }, [associations, contactsError]);
}

/**
 * Note, file and pin writes through the v1 actions. The v1 client middleware
 * resolves a failed request to its `_FAILURE` action; these reject instead.
 */
export function useResourceAssociationActions(resourceId: number) {
  const dispatch = useDispatch();
  const runAction = async (action: Parameters<typeof dispatch>[0]) => {
    const result = await dispatch(action);
    if (result.type.endsWith('_FAILURE')) {
      throw result.error;
    }
    return result;
  };
  const run = async (action: Parameters<typeof dispatch>[0]) => {
    await runAction(action);
  };
  return {
    createNote: (note: string) => run(createContactNote({ note }, resourceId)),
    deleteNote: (noteId: number) => run(deleteContactNote(noteId, resourceId)),
    createDocument: (payload: { file_id: string; url: string }) =>
      run(createContactDocument(payload, resourceId)),
    /** The pin as the server made it, with its id. */
    createPin: async (payload: {
      file_id?: string;
      pin_type: string;
      url: string;
    }): Promise<MediaPin> =>
      (await runAction(createContactPin(payload, resourceId))).result,
    deletePin: (pinId: number) => run(deleteContactPin(pinId, resourceId)),
    /** The pin with its whole new list of tags; a new tag has no id yet. */
    updatePinTags: (pin: MediaPin, tags: { id?: number; tag: string }[]) =>
      run(
        updateContactPin(
          { ...pin, tags },
          { contact_id: resourceId, pin_id: pin.id }
        )
      ),
    /** Every live pin's id in the new order. */
    reorderPins: (orderedIds: number[]) =>
      run(
        updateContactPins(
          { contact_id: resourceId },
          orderedIds.map((id, order) => ({ id, order }))
        )
      ),
    deleteDocument: (documentId: number) =>
      run(deleteContactDocument(documentId, resourceId)),
    reorderDocuments: (orderedIds: number[]) =>
      run(reorderContactDocuments(orderedIds, resourceId))
  };
}
